import { Table, TableBody, TableHead, TableRow } from '@mui/material';

import GeneralTableBox from './GeneralTableBox';
import SortableHeadTableCell from './SortableHeadTableCell';
import CompactTableCell from './CompactTableCell';
import NoDataSkeleton from '../skeletons/NoDataSkeleton';

const HEAD_SX = { px: 1, py: 0.75, fontSize: 13, lineHeight: 1.2, '&&': { whiteSpace: 'normal' } };

/**
 * Tabla de datos para espacios angostos (drawers con sidebar, paneles laterales): ocupa el ancho
 * del contenedor sin scroll horizontal. Usa celdas de dos líneas (dato principal y secundario)
 * para combinar columnas, padding reducido y encabezados que bajan de línea en lugar de cortarse.
 * El orden lo resuelve quien la usa (`useTableSort` + `sortRows`) y recibe las filas ya ordenadas.
 * @param {Object} props
 * @param {Array<Object>} props.columns Columnas `{ key, label, width?, align?, sortKey?,
 *   sortable?, render?, secondary?, editable? }`. Sin `width`, la columna toma el ancho restante.
 * @param {Array<Object>} props.rows Filas a mostrar.
 * @param {Function} props.getRowKey Recibe la fila y devuelve su key.
 * @param {{key: string, direction: string}|null} [props.sort] Orden vigente.
 * @param {Function} [props.onSort] Recibe la clave de la columna clickeada.
 * @param {string} props.emptyMessage Texto cuando no hay filas.
 * @param {number} [props.minTableWidth] Ancho mínimo; debajo de ese ancho recién aparece scroll.
 * @returns {JSX.Element}
 */
const CompactTable = ({ columns, rows, getRowKey, sort, onSort, emptyMessage, minTableWidth }) => (
  <GeneralTableBox fixedLayout minTableWidth={minTableWidth} hasMaxHeight={false}>
    <Table size="small" stickyHeader>
      <TableHead>
        <TableRow>
          {columns.map((column) => (
            <SortableHeadTableCell
              key={column.key}
              sortKey={column.sortable === false ? undefined : column.sortKey || column.key}
              sort={sort}
              onSort={onSort}
              sx={{ ...HEAD_SX, width: column.width, textAlign: column.align || 'left' }}
            >
              {column.label}
            </SortableHeadTableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => (
          <TableRow hover key={getRowKey(row)}>
            {columns.map((column) => (
              <CompactTableCell key={column.key} column={column} row={row} />
            ))}
          </TableRow>
        ))}
        {!rows.length && (
          <NoDataSkeleton colSpan={columns.length} showSubtitle={false} message={emptyMessage} />
        )}
      </TableBody>
    </Table>
  </GeneralTableBox>
);

export default CompactTable;
